import React from 'react';
import {
    IconButton,
    Typography,
    Box,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { QuestionDTO } from "../../../../types/ApplicationQuestion";
import QuestionTypePill from '../../../Common/QuestionTypePill';
import MatchingImportancePill from '../../../Common/MatchingImportancePill';

interface SingleQuestionPanelProps {
    question: QuestionDTO;
    onEdit: (question: QuestionDTO) => void;
    onDelete: (question: QuestionDTO) => void;
    onMoveUp?: (question: QuestionDTO) => void;
    onMoveDown?: (question: QuestionDTO) => void;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
    readOnly?: boolean;
}

const SingleQuestionPanel: React.FC<SingleQuestionPanelProps> = ({
    question,
    onEdit,
    onDelete,
    onMoveUp,
    onMoveDown,
    canMoveUp = false,
    canMoveDown = false,
    readOnly = false
}) => {
    return (
        <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1">
                            {question.question_text}
                        </Typography>
                        <QuestionTypePill type={question.question_type} size="small" />
                        {question.matching_importance && (
                            <MatchingImportancePill importance={question.matching_importance} size="small" />
                        )}
                    </Box>
                </Box>

                {!readOnly && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                        {onMoveUp && (
                            <Tooltip title="Move up">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => onMoveUp(question)}
                                        disabled={!canMoveUp}
                                        sx={{ color: canMoveUp ? 'action.active' : 'action.disabled' }}
                                    >
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        {onMoveDown && (
                            <Tooltip title="Move down">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => onMoveDown(question)}
                                        disabled={!canMoveDown}
                                        sx={{ color: canMoveDown ? 'action.active' : 'action.disabled' }}
                                    >
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Edit question">
                            <IconButton
                                size="small"
                                onClick={() => onEdit(question)}
                                sx={{ color: 'primary.main' }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete question">
                            <IconButton
                                size="small"
                                onClick={() => onDelete(question)}
                                sx={{ color: 'error.main' }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SingleQuestionPanel;